import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Menu, X, ShieldCheck } from "lucide-react";
import { motion, useScroll } from "framer-motion";
import { Logo } from "./Logo";

const links = [
  { label: "About", href: "#about", testid: "nav-link-about" },
  { label: "Services", href: "#services", testid: "nav-link-services" },
  { label: "Projects", href: "#projects", testid: "nav-link-projects" },
  { label: "Contact", href: "#contact", testid: "nav-link-contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-300 ${
        scrolled || open ? "border-b border-white/10 bg-black/80 backdrop-blur-xl" : "border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <a href="#top" aria-label="Gativan Office home">
          <Logo />
        </a>

        <nav className="hidden items-center gap-8 lg:flex">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              className="font-mono text-xs uppercase tracking-[0.2em] text-[#9CA3AF] transition-colors hover:text-[#FFFFFF]"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            data-testid="nav-admin-button"
            aria-label="Admin portal"
            className="hidden items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-[#9CA3AF] transition-colors hover:border-[#FFFFFF] hover:text-[#FFFFFF] sm:flex"
          >
            <ShieldCheck size={14} /> Admin
          </Link>
          <a
            href="tel:+917709322253"
            data-testid="nav-call-button"
            className="flex items-center gap-2 rounded-full bg-[#FFFFFF] px-5 py-2.5 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-black transition-transform duration-200 hover:scale-[1.04] active:scale-95"
          >
            <Phone size={14} /> <span className="hidden sm:inline">77093 22253</span><span className="sm:hidden">Call</span>
          </a>
          <button
            data-testid="nav-mobile-menu-button"
            aria-label="Toggle menu"
            onClick={() => setOpen(!open)}
            className="text-[#F3F4F6] lg:hidden"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="border-t border-white/10 bg-black/95 px-5 py-6 backdrop-blur-xl lg:hidden">
          <div className="flex flex-col gap-5">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`mobile-${l.testid}`}
                onClick={() => setOpen(false)}
                className="font-display text-2xl font-bold uppercase tracking-tight text-[#F3F4F6] hover:text-[#FFFFFF]"
              >
                {l.label}
              </a>
            ))}
            <Link to="/admin" data-testid="mobile-nav-admin-button" className="font-mono text-xs uppercase tracking-[0.2em] text-[#9CA3AF]">
              Admin Portal
            </Link>
          </div>
        </nav>
      )}
      <motion.div
        style={{ scaleX: scrollYProgress }}
        className="absolute bottom-0 left-0 h-[2px] w-full origin-left bg-white/80"
        aria-hidden="true"
      />
    </header>
  );
};
